/**
 * Booking API Contract Schemas
 *
 * Defines Zod schemas for booking-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend (React forms).
 */

import { z } from 'zod';
import { createPaginatedSchema } from '../types/api-response';
import { AssignUnitsSchema, InventoryCategoryEnum } from './inventory';
import { PaymentStatusEnum } from './payments';

// ============================================
// ENUMS
// ============================================

export const BookingStatusEnum = z.enum([
  'PENDING',
  'CONFIRMED',
  'CHECKED_IN',
  'CANCELLED',
  'COMPLETED',
  'NO_SHOW',
]);
export type BookingStatusValue = z.infer<typeof BookingStatusEnum>;

// ============================================
// AVAILABILITY SCHEMAS
// ============================================

export const AvailabilityQuerySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
  inventoryItemId: z.string().uuid().optional(),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

/**
 * Date Range Availability Query
 * Used to fetch availability summary for multiple dates in a single request
 */
export const DateRangeAvailabilityQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Start date must be YYYY-MM-DD format'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'End date must be YYYY-MM-DD format'),
});
export type DateRangeAvailabilityQuery = z.infer<typeof DateRangeAvailabilityQuerySchema>;

/**
 * Date Availability Level
 * Categorizes availability for a single date
 */
export const DateAvailabilityLevelSchema = z.enum([
  'full', // 100% - No bookings or plenty of availability
  'high', // 70-99% - High availability
  'medium', // 40-69% - Moderate availability
  'low', // 1-39% - Limited availability
  'unavailable', // 0% - Fully booked
  'closed', // Venue closed on this date
]);
export type DateAvailabilityLevel = z.infer<typeof DateAvailabilityLevelSchema>;

/**
 * Single Date Availability Summary
 * Provides quick availability overview for a specific date
 */
export const DateAvailabilitySummarySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
  level: DateAvailabilityLevelSchema,
  totalSlots: z.number().int().min(0),
  availableSlots: z.number().int().min(0),
  isClosed: z.boolean(),
});
export type DateAvailabilitySummary = z.infer<typeof DateAvailabilitySummarySchema>;

/**
 * Date Range Availability Response
 * Returns availability summary for all dates in the requested range
 */
export const DateRangeAvailabilityResponseSchema = z.object({
  venueId: z.string().uuid(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  bookingMode: z.enum(['inventory', 'capacity']),
  dates: z.array(DateAvailabilitySummarySchema),
});
export type DateRangeAvailabilityResponse = z.infer<typeof DateRangeAvailabilityResponseSchema>;

// ============================================
// BOOKING SCHEMAS
// ============================================

const BookingInventoryItemSchema = z.object({
  venueInventoryId: z.string().uuid('Invalid inventory ID'),
  quantity: z.number().int().min(1, 'Quantity must be at least 1'),
});

const BookingFoodItemSchema = z.object({
  foodItemId: z.string().uuid('Invalid food item ID'),
  quantity: z.number().int().min(1, 'Quantity must be at least 1'),
});

const BookingAddonItemSchema = z.object({
  addonItemId: z.string().uuid('Invalid addon item ID'),
  venueInventoryId: z.string().uuid('Invalid venue inventory ID'),
  quantity: z.number().int().min(1).max(10),
});

export const CreateBookingSchema = z.object({
  venueId: z.string().uuid('Invalid venue ID'),
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
  startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
  endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
  guestCount: z.number().int().min(1, 'Guest count must be at least 1').optional(),
  specialRequests: z.string().max(500, 'Special requests must be under 500 characters').optional(),
  couponCode: z.string().optional(),
  useWalletCredits: z.boolean().optional(),
  inventoryItems: z.array(BookingInventoryItemSchema).optional(),
  foodItems: z.array(BookingFoodItemSchema).optional(),
  addons: z.array(BookingAddonItemSchema).optional(),
});
export type CreateBookingRequest = z.infer<typeof CreateBookingSchema>;

export const CancelBookingSchema = z.object({
  reason: z.string().max(500, 'Reason must be under 500 characters').optional(),
});
export type CancelBookingRequest = z.infer<typeof CancelBookingSchema>;

export const BookingFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  status: BookingStatusEnum.optional(),
  fromDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  toDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  search: z.string().optional(),
});
export type BookingFilterQuery = z.infer<typeof BookingFilterSchema>;

export const VenueBookingFilterSchema = BookingFilterSchema.extend({
  venueId: z.string().uuid().optional(),
});
export type VenueBookingFilterQuery = z.infer<typeof VenueBookingFilterSchema>;

// ============================================
// WALK-IN BOOKING SCHEMAS
// ============================================

export const CreateWalkInBookingSchema = z.object({
  venueId: z.string().uuid('Invalid venue ID'),
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
  startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
  endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),

  // Customer info (guest or existing)
  customerType: z.enum(['EXISTING', 'GUEST']),
  customerId: z.string().uuid().optional(),
  guestCustomer: z
    .object({
      name: z.string().min(1),
      phone: z.string().min(10),
    })
    .optional(),

  guestCount: z.number().int().min(1).optional(),

  // Equipment selection
  inventoryItems: z.array(
    z.object({
      venueInventoryId: z.string().uuid(),
      quantity: z.number().int().min(1),
    })
  ),

  // Addon items (Bug #8: addon support for walk-in bookings)
  addons: z.array(BookingAddonItemSchema).optional(),

  // Payment - now supports partial payments
  paymentStatus: z.enum(['PAID', 'PENDING', 'PARTIAL']),
  paymentMethod: z.enum(['CASH', 'CARD', 'UPI', 'ONLINE']).optional(),
  amountPaid: z.number().min(0).optional(), // Actual amount collected for partial payment
  amountOverride: z.number().min(0).optional(), // Vendor can override calculated total

  // Vendor Custom Discount
  vendorDiscount: z.number().min(0).optional(),
  vendorDiscountType: z.enum(['PERCENTAGE', 'FIXED']).optional(),
  vendorDiscountReason: z.string().max(500).optional(),

  // Coupon support (Feature 4)
  couponCode: z.string().max(20).optional(),

  specialRequests: z.string().max(500).optional(),
  autoCheckIn: z.boolean().optional(),
  unitAssignments: AssignUnitsSchema.shape.assignments.optional(),
});
export type CreateWalkInBookingRequest = z.infer<typeof CreateWalkInBookingSchema>;

// ============================================
// RESPONSE SCHEMAS
// ============================================

export const SlotAvailabilitySchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
  available: z.number(),
  isAvailable: z.boolean(),
});
export type SlotAvailability = z.infer<typeof SlotAvailabilitySchema>;

export const AddonAvailabilitySchema = z.object({
  addonItemId: z.string(),
  venueInventoryId: z.string(), // VenueInventory.id of the addon instance (addon's own VenueInventory)
  addonName: z.string(),
  includedQuantity: z.number().default(0),
  maxAdditionalQuantity: z.number().optional(),
  pricePerHour: z.number().optional(),
  pricePerHalfHour: z.number().optional(),
  isRequired: z.boolean().default(false),
  availableQuantity: z.number(),
});
export type AddonAvailability = z.infer<typeof AddonAvailabilitySchema>;

export const InventorySlotAvailabilitySchema = z.object({
  inventoryItemId: z.string(),
  venueInventoryId: z.string(),
  itemName: z.string(),
  category: InventoryCategoryEnum,
  totalQuantity: z.number(),
  pricePerHour: z.number().optional(),
  pricePerHalfHour: z.number().optional(),
  maxPlayers: z.number().optional(),
  slots: z.array(SlotAvailabilitySchema),
  addons: z.array(AddonAvailabilitySchema).optional(),
});
export type InventorySlotAvailability = z.infer<typeof InventorySlotAvailabilitySchema>;

export const AvailabilityResponseSchema = z.object({
  venueId: z.string(),
  date: z.string(),
  bookingMode: z.enum(['inventory', 'capacity']),
  slots: z.array(SlotAvailabilitySchema),
  inventoryAvailability: z.array(InventorySlotAvailabilitySchema).optional(),
  maxCapacity: z.number().optional(),
  totalQuantity: z.number().optional(),
});
export type AvailabilityResponse = z.infer<typeof AvailabilityResponseSchema>;

export const PriceBreakdownSchema = z.object({
  baseAmount: z.number().optional(),
  inventoryTotal: z.number().optional(),
  foodSubtotal: z.number().optional(),
  addonSubtotal: z.number().default(0),
  subtotal: z.number(),
  /** Coupon discount only. See totalDiscount for all discounts combined. */
  discount: z.number().default(0),
  refereeDiscount: z.number().optional(),
  refereeDiscountApplied: z.boolean().optional(),
  /** Sum of coupon discount and referee discount. */
  totalDiscount: z.number().int().nonnegative().optional(),
  serviceFee: z.number().default(0),
  serviceFeePercent: z.number().optional(),
  taxes: z.number().default(0),
  gstPercent: z.number().optional(),
  gstEnabled: z.boolean().optional(),
  totalAmount: z.number().optional(),
  currency: z.string(),
  foodDiscount: z.number().default(0),
  couponApplied: z.boolean().default(false),
  couponCode: z.string().optional(),
  foodItemPrices: z.record(z.number()).optional(),
  itemizedPrices: z
    .array(
      z.object({
        venueInventoryId: z.string(),
        itemName: z.string(),
        quantity: z.number(),
        priceForDuration: z.number(),
        totalPrice: z.number(),
      })
    )
    .optional(),
  addonBreakdown: z.number().default(0),
});
export type PriceBreakdown = z.infer<typeof PriceBreakdownSchema>;

export const BookingInventoryItemResponseSchema = z.object({
  id: z.string(),
  venueInventoryId: z.string(),
  inventoryItem: z.object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
  }),
  quantity: z.number(),
  pricePerUnit: z.number(),
  totalPrice: z.number(),
});
export type BookingInventoryItemResponse = z.infer<typeof BookingInventoryItemResponseSchema>;

export const BookingFoodItemResponseSchema = z.object({
  id: z.string(),
  foodItemId: z.string(),
  foodItem: z.object({
    id: z.string(),
    name: z.string(),
    category: z.string().nullish(),
  }),
  quantity: z.number(),
  unitPrice: z.number(),
  totalPrice: z.number(),
});
export type BookingFoodItemResponse = z.infer<typeof BookingFoodItemResponseSchema>;

export const BookingResponseSchema = z.object({
  id: z.string(),
  venueId: z.string(),
  venue: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    address: z.string(),
    city: z.object({ name: z.string() }).optional(),
    coverImage: z.string().nullish(), // Accept null or undefined
  }),
  customerId: z.string(),
  customer: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      phone: z.string().nullish(), // Phone number (returned by vendor endpoints)
    })
    .optional(),
  bookingDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  duration: z.number(),
  baseAmount: z.number(),
  taxes: z.number(),
  discount: z.number(),
  totalAmount: z.number(),
  walletCreditsUsed: z.number().nullish(), // Accept null or undefined
  amountToPay: z.number().nullish(), // Accept null or undefined
  currency: z.string(),
  status: BookingStatusEnum,
  guestCount: z.number().nullish(), // Accept null or undefined
  specialRequests: z.string().nullish(), // Accept null or undefined
  inventoryItems: z.array(BookingInventoryItemResponseSchema).optional(),
  foodItems: z.array(BookingFoodItemResponseSchema).optional(),
  cancelledAt: z.string().nullish(), // Accept null or undefined
  cancellationReason: z.string().nullish(), // Accept null or undefined
  confirmationCode: z.string().nullish(), // LOV{YY}-XXXXXX format
  // Vendor check-in fields
  checkInTime: z.string().nullish(), // When customer checked in
  checkInMethod: z
    .enum(['QR_SCAN', 'MANUAL', 'CODE_ENTRY', 'PHONE_LOOKUP', 'NAME_SEARCH'])
    .nullish(), // How they checked in
  paidAt: z.string().nullish(), // When payment was collected
  paymentMethod: z.enum(['CASH', 'CARD', 'UPI', 'ONLINE']).nullish().catch(null), // How payment was collected
  paymentStatus: z
    .union([PaymentStatusEnum, z.enum(['PAID', 'PENDING', 'PARTIAL', 'DEPOSIT_PAID'])])
    .optional(), // Payment status (from transformBookingResponse or standard payment status)
  amountDue: z.number().optional(), // Remaining amount to pay (from transformBookingResponse)
  amountPaid: z.number().optional(), // Actual amount paid for partial payments
  amountOverride: z.number().optional(), // Vendor overridden amount
  balanceDue: z.number().optional(), // Calculated: totalAmount (or override) - amountPaid
  isOverdue: z.boolean().optional(), // >15min past start time and not checked in
  isNoShowEligible: z.boolean().optional(), // >60min past start time and not checked in
  adminCouponDiscount: z.number().optional(), // Platform-absorbed discount amount (admin coupons)
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BookingResponse = z.infer<typeof BookingResponseSchema>;

// ============================================
// VENDOR CHECK-IN SCHEMAS
// ============================================

export const BookingPaymentStatusEnum = z.enum(['PAID', 'PENDING', 'PARTIAL']);
export type BookingPaymentStatusValue = z.infer<typeof BookingPaymentStatusEnum>;

export const VendorCheckInSchema = z.object({
  // Empty for manual check-in, or include qrCode for QR scan
  qrCode: z.string().optional(),
  // New: confirmation code for code-entry check-in
  confirmationCode: z.string().optional(),
  // New: check-in method
  method: z.enum(['QR_SCAN', 'MANUAL', 'CODE_ENTRY', 'PHONE_LOOKUP', 'NAME_SEARCH']).optional(),
  // New: early check-in acknowledgment (required when checking in >30 mins early)
  earlyCheckInAcknowledged: z.boolean().optional(),
  // Table Management: unit assignments at check-in
  unitAssignments: AssignUnitsSchema.shape.assignments.optional(),
});
export type VendorCheckInRequest = z.infer<typeof VendorCheckInSchema>;

export const VendorCheckInResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  checkInTime: z.string(),
  bookingId: z.string(),
  // New: early check-in metadata
  isEarlyCheckIn: z.boolean().optional(),
  earlyByMinutes: z.number().optional(),
});
export type VendorCheckInResponse = z.infer<typeof VendorCheckInResponseSchema>;

export const MarkAsPaidSchema = z.object({
  paymentMethod: z.enum(['CASH', 'UPI', 'CARD']),
  amountPaid: z.number().min(0).optional(), // For partial payments
  amountOverride: z.number().min(0).optional(), // Vendor can override total
});
export type MarkAsPaidRequest = z.infer<typeof MarkAsPaidSchema>;

export const MarkAsPaidResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  paidAt: z.string(),
  bookingId: z.string(),
});
export type MarkAsPaidResponse = z.infer<typeof MarkAsPaidResponseSchema>;

export const ActiveSessionSchema = z.object({
  bookingId: z.string(),
  customerName: z.string(),
  customerPhone: z.string(), // masked (last 4 digits only)
  checkInTime: z.string(),
  endTime: z.string(),
  remainingMinutes: z.number(),
  paymentStatus: BookingPaymentStatusEnum,
  amountDue: z.number(),
  // Phase 3A: Break management fields
  hasActiveBreak: z.boolean(),
  breakDurationMinutes: z.number(),
  // Customer return indicator
  visitCount: z.number().optional(),
  // Overtime fields
  overtimeStartedAt: z.string().optional(),
  graceMinutes: z.number().optional(),
  overtimeMinutes: z.number().optional(),
  overtimeChargeDecision: z.string().nullable().optional(),
});
export type ActiveSession = z.infer<typeof ActiveSessionSchema>;

export const ActiveSessionsResponseSchema = z.array(ActiveSessionSchema);
export type ActiveSessionsResponse = z.infer<typeof ActiveSessionsResponseSchema>;

// ============================================
// CUSTOMER ACTIVE SESSION STATUS SCHEMA
// Used by GET /bookings/:id/active-status (customer polling endpoint)
// ============================================

export const CustomerActiveSessionStatusSchema = z.object({
  bookingId: z.string(),
  status: z.string(), // CHECKED_IN | COMPLETED
  endTime: z.string(), // ISO
  remainingMinutes: z.number(), // negative when in overtime
  overtimeStartedAt: z.string().nullable(),
  graceMinutes: z.number(),
  overtimeMinutes: z.number(),
  overtimeChargeDecision: z.string().nullable(), // null | 'CHARGED' | 'WAIVED'
  pendingChargeAmount: z.number().nullable(), // if CHARGED, the amount owed
});
export type CustomerActiveSessionStatus = z.infer<typeof CustomerActiveSessionStatusSchema>;

// ============================================
// VENDOR ACTIVE SESSIONS SCHEMAS
// ============================================

export const ExtensionPaymentUpdateSchema = z.object({
  extensionId: z.string(),
  paymentMethod: z.enum(['CASH', 'UPI', 'CARD']),
  paymentStatus: z.enum(['PAID', 'PENDING']),
});
export type ExtensionPaymentUpdate = z.infer<typeof ExtensionPaymentUpdateSchema>;

export const VendorEndSessionRequestSchema = z.object({
  paymentMethod: z.enum(['CASH', 'UPI', 'CARD']).optional(),
  paymentUpdates: z.array(ExtensionPaymentUpdateSchema).optional(),
});
export type VendorEndSessionRequest = z.infer<typeof VendorEndSessionRequestSchema>;

export const VendorEndSessionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  completedAt: z.string(),
  bookingId: z.string(),
  invoiceGenerated: z.boolean(),
});
export type VendorEndSessionResponse = z.infer<typeof VendorEndSessionResponseSchema>;

// ============================================
// VENDOR BOOKING MANAGEMENT SCHEMAS
// ============================================

// Vendor bookings list query (extends existing BookingFilterSchema pattern)
export const VendorBookingsQuerySchema = z.object({
  status: BookingStatusEnum.optional(),
  venueId: z.string().uuid().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  customerSearch: z.string().optional(), // Search by name, email, or phone
  minAmount: z.number().optional(),
  maxAmount: z.number().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  sortBy: z.enum(['createdAt', 'bookingDate', 'amount']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  outstandingBalance: z.boolean().optional(), // Filter for PARTIAL payments with balanceDue > 0
});
export type VendorBookingsQuery = z.infer<typeof VendorBookingsQuerySchema>;

// Bulk status update request
export const BulkStatusUpdateSchema = z.object({
  bookingIds: z.array(z.string().uuid()).min(1).max(50),
  status: BookingStatusEnum,
  reason: z.string().max(500).optional(), // Required for cancellation
  notes: z.string().max(1000).optional(),
});
export type BulkStatusUpdateRequest = z.infer<typeof BulkStatusUpdateSchema>;

// Bulk status update response
export const BulkStatusUpdateResponseSchema = z.object({
  success: z.array(z.string().uuid()),
  failed: z.array(
    z.object({
      bookingId: z.string().uuid(),
      error: z.string(),
    })
  ),
  message: z.string(),
});
export type BulkStatusUpdateResponse = z.infer<typeof BulkStatusUpdateResponseSchema>;

// Paginated bookings response (wrapped in standard API envelope)
export const PaginatedBookingsResponseSchema = createPaginatedSchema(BookingResponseSchema);
export type PaginatedBookingsResponse = z.infer<typeof PaginatedBookingsResponseSchema>;

// ============================================
// BOOKING CONFIRMATION CONTEXT SCHEMAS
// ============================================

/**
 * Booking details section of the confirmation context.
 * Returned as part of GET /bookings/:id/confirmation-context
 */
export const ConfirmationBookingSchema = z.object({
  id: z.string().uuid(),
  confirmationCode: z.string().nullable(), // LOV{YY}-XXXXXX format
  venueName: z.string(),
  venueId: z.string().uuid(),
  venueSlug: z.string(),
  venueStateSlug: z.string(),
  venueCitySlug: z.string(),
  venueAddress: z.string(),
  venuePhoto: z.string().nullable(), // Cover image URL
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
  startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
  endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
  durationMinutes: z.number().int().min(0),
  addons: z.array(
    z.object({
      name: z.string(),
      quantity: z.number().int().min(1),
      price: z.number().min(0),
    })
  ),
  foodItems: z
    .array(
      z.object({
        name: z.string(),
        quantity: z.number().int().min(1),
        unitPrice: z.number().min(0),
      })
    )
    .default([]),
  totalPaid: z.number().min(0),
  currency: z.literal('INR'),
});
export type ConfirmationBooking = z.infer<typeof ConfirmationBookingSchema>;

/**
 * Loyalty tier enum for confirmation context
 */
export const ConfirmationTierEnum = z.enum(['BRONZE', 'SILVER', 'GOLD', 'PLATINUM']);
export type ConfirmationTier = z.infer<typeof ConfirmationTierEnum>;

/**
 * Badge shape used in the confirmation loyalty section.
 * Mirrors the Badge type from gamification without importing gamification schemas.
 */
export const ConfirmationBadgeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  iconUrl: z.string(),
  rarity: z.enum(['COMMON', 'RARE', 'EPIC', 'LEGENDARY']),
});
export type ConfirmationBadge = z.infer<typeof ConfirmationBadgeSchema>;

/**
 * Loyalty snapshot section of the confirmation context.
 * Captures the XP, tier, badges, and streak earned specifically from this booking.
 */
export const ConfirmationLoyaltySchema = z.object({
  xpEarned: z.number().int().min(0),
  totalXp: z.number().int().min(0),
  currentTier: ConfirmationTierEnum,
  tierChanged: z.boolean(),
  previousTier: ConfirmationTierEnum.nullable(),
  nextTierXp: z.number().int().min(0),
  progressPercent: z.number().min(0).max(100),
  badgesUnlocked: z.array(ConfirmationBadgeSchema),
  streakEffect: z
    .object({
      active: z.boolean(),
      currentStreak: z.number().int().min(0),
    })
    .nullable(),
  isNewUser: z.boolean(),
});
export type ConfirmationLoyalty = z.infer<typeof ConfirmationLoyaltySchema>;

/**
 * Upsell and discovery section of the confirmation context.
 * Three discovery categories: related venues, upcoming slots, and available add-ons.
 */
export const ConfirmationUpsellsSchema = z.object({
  relatedVenues: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
      stateSlug: z.string(),
      citySlug: z.string(),
      category: z.string(),
      distanceKm: z.number().min(0),
      thumbnailUrl: z.string().nullable(),
    })
  ),
  upcomingSlots: z.array(
    z.object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format'),
      startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
      endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be HH:MM format'),
    })
  ),
  availableAddons: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      pricePerUnit: z.number().min(0),
      unit: z.string(), // e.g. 'per hour', 'per item'
    })
  ),
});
export type ConfirmationUpsells = z.infer<typeof ConfirmationUpsellsSchema>;

/**
 * Booking Confirmation Context Schema.
 * Validates the full response from GET /bookings/:id/confirmation-context.
 * Aggregates booking details, loyalty snapshot, and upsell discovery data
 * into a single type-safe contract for the post-booking confirmation screen.
 */
export const BookingConfirmationContextSchema = z.object({
  booking: ConfirmationBookingSchema,
  loyalty: ConfirmationLoyaltySchema,
  upsells: ConfirmationUpsellsSchema,
});
export type BookingConfirmationContext = z.infer<typeof BookingConfirmationContextSchema>;

// ============================================
// SHIFT SUMMARY SCHEMAS
// ============================================

export const ShiftSummaryRequestSchema = z.object({
  venueId: z.string().uuid(),
  /** ISO date string — defaults to today IST */
  date: z.string().optional(),
  /** Staff user ID — defaults to current user */
  staffId: z.string().optional(),
});
export type ShiftSummaryRequest = z.infer<typeof ShiftSummaryRequestSchema>;

export const ShiftSummarySchema = z.object({
  date: z.string(),
  staffName: z.string(),
  staffId: z.string(),
  venueId: z.string(),
  venueName: z.string(),
  sessionsManaged: z.number(),
  totalRevenue: z.number(),
  currency: z.string(),
  paymentBreakdown: z.object({
    online: z.object({ count: z.number(), amount: z.number() }),
    cash: z.object({ count: z.number(), amount: z.number() }),
    upi: z.object({ count: z.number(), amount: z.number() }),
    card: z.object({ count: z.number(), amount: z.number() }),
    pending: z.object({ count: z.number(), amount: z.number() }),
  }),
  avgSessionDurationMinutes: z.number(),
  checkInsPerformed: z.number(),
  sessionsEnded: z.number(),
  extensionsCreated: z.number(),
});
export type ShiftSummary = z.infer<typeof ShiftSummarySchema>;

// ---- Session Extension Schemas ----

// Vendor-initiated extension request (full walk-in parity)
export const VendorSessionExtensionRequestSchema = z.object({
  durationMinutes: z.number().int().min(30).max(480),
  vendorDiscount: z.number().min(0).optional(),
  vendorDiscountType: z.enum(['PERCENTAGE', 'FIXED']).optional(),
  vendorDiscountReason: z.string().trim().max(500).optional(),
  paymentMethod: z.string().optional(),
  paymentStatus: z.enum(['PAID', 'PENDING', 'PARTIAL']).optional(),
  amountPaid: z.number().min(0).optional(),
  amountOverride: z.number().min(0).optional(),
});
export type VendorSessionExtensionRequest = z.infer<typeof VendorSessionExtensionRequestSchema>;

// Extension price quote response
export const ExtensionPriceQuoteSchema = z.object({
  isAvailable: z.boolean(),
  maxAvailableMinutes: z.number().optional(),
  unavailableReason: z.string().optional(),
  durationMinutes: z.number(),
  baseAmount: z.number(),
  taxes: z.number(),
  serviceFee: z.number(),
  totalAmount: z.number(),
  currency: z.string().default('INR'),
});
export type ExtensionPriceQuote = z.infer<typeof ExtensionPriceQuoteSchema>;

// Full extension response
export const SessionExtensionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  bookingId: z.string(),
  extensionId: z.string(),
  previousEndTime: z.string(),
  newEndTime: z.string(),
  durationMinutes: z.number(),
  baseAmount: z.number(),
  taxes: z.number(),
  serviceFee: z.number(),
  totalAmount: z.number(),
  vendorDiscountAmount: z.number().optional(),
  paymentStatus: z.string(),
  amountPaid: z.number().optional(),
  balanceDue: z.number().optional(),
});
export type SessionExtensionResponse = z.infer<typeof SessionExtensionResponseSchema>;

// ========================
// Table Management (Phase 1)
// ========================

export const SessionExtensionLineItemSchema = z.object({
  extensionId: z.string(),
  durationMinutes: z.number(),
  totalAmount: z.number(),
  paymentStatus: z.enum(['PENDING', 'PAID', 'PARTIAL', 'FAILED', 'WAIVED']),
  paymentMethod: z.string().nullable(),
  createdAt: z.string(),
});
export type SessionExtensionLineItem = z.infer<typeof SessionExtensionLineItemSchema>;

export const FloorUnitSchema = z.object({
  unitId: z.string().uuid(),
  label: z.string(),
  availabilityMode: z.enum(['ALL', 'WALKIN_ONLY']),
  status: z.enum(['AVAILABLE', 'OCCUPIED', 'RESERVED', 'INACTIVE']),
  currentSession: z
    .object({
      bookingId: z.string(),
      customerName: z.string(),
      checkInTime: z.string(),
      endTime: z.string(),
      remainingMinutes: z.number(),
      paymentStatus: z.string(),
      amountDue: z.number(),
      hasActiveBreak: z.boolean(),
      extensions: z.array(SessionExtensionLineItemSchema).optional(),
    })
    .optional(),
  upcomingReservation: z
    .object({
      bookingId: z.string(),
      customerName: z.string(),
      startTime: z.string(),
    })
    .optional(),
});
